import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { RESUME_URL } from '../data/constants';

const Nav = ({ navItems, activeSection, onScrollTo, mode = 'portfolio', rightSlot }) => {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const hamburgerRef = useRef(null);
  const mobileNavRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    let ticking = false;
    const handle = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const total = document.documentElement.scrollHeight - window.innerHeight;
        setScrollProgress(total > 0 ? window.scrollY / total : 0);
        ticking = false;
      });
    };
    window.addEventListener('scroll', handle, { passive: true });
    return () => window.removeEventListener('scroll', handle);
  }, []);

  // Close mobile nav on Escape key + focus trap
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape' && mobileNavOpen) {
      setMobileNavOpen(false);
      hamburgerRef.current?.focus();
      return;
    }
    // Focus trap within mobile nav
    if (e.key === 'Tab' && mobileNavOpen && mobileNavRef.current) {
      const focusable = mobileNavRef.current.querySelectorAll(
        'button, a, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
  }, [mobileNavOpen]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Auto-focus first nav link when mobile nav opens
  useEffect(() => {
    if (mobileNavOpen && mobileNavRef.current) {
      const firstLink = mobileNavRef.current.querySelector('button, a');
      firstLink?.focus();
    }
  }, [mobileNavOpen]);

  const handleNav = (id) => {
    if (mode === 'detail') {
      navigate(`/#${id}`);
    } else {
      onScrollTo(id);
    }
    setMobileNavOpen(false);
  };

  const handleLogoClick = () => {
    if (mode === 'detail') {
      navigate('/');
    } else {
      onScrollTo('hero');
    }
    setMobileNavOpen(false);
  };

  return (
    <header className="fixed top-0 w-full z-50">
      {/* Progress bar — uses scaleX transform for GPU-accelerated animation */}
      <div className="h-px bg-border/40 relative overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 right-0 bg-green origin-left"
          style={{
            transform: `scaleX(${scrollProgress})`,
            boxShadow: '0 0 8px rgba(var(--color-green), 0.4), 0 0 2px rgba(var(--color-green), 0.6)',
          }}
          role="progressbar"
          aria-valuenow={Math.round(scrollProgress * 100)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Page scroll progress"
        />
      </div>

      <div className="bg-surface/80 backdrop-blur-2xl border-b border-border/40">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <button
            onClick={handleLogoClick}
            className="font-mono text-sm font-bold tracking-tight hover:text-green transition-colors duration-200"
          >
            yehor<span className="text-green animate-blink">_</span>
          </button>

          <nav className="hidden md:flex items-center gap-1" aria-label="Main navigation">
            {navItems.map(({ id, label }) => (
              <button
                key={id}
                onClick={() => handleNav(id)}
                aria-current={activeSection === id ? 'page' : undefined}
                className={`relative px-3 py-1.5 text-sm font-medium transition-all duration-200 rounded-md ${
                  activeSection === id
                    ? 'text-green bg-green/5'
                    : 'text-txt-secondary hover:text-txt hover:bg-raised/60'
                }`}
              >
                {label}
                {activeSection === id && (
                  <motion.div
                    layoutId="nav-indicator"
                    className="absolute bottom-0 left-3 right-3 h-px bg-green"
                    style={{ boxShadow: '0 0 4px rgba(var(--color-green), 0.3)' }}
                    transition={{ type: 'spring', bounce: 0.15, duration: 0.5 }}
                  />
                )}
              </button>
            ))}
            <div className="w-px h-4 bg-border mx-2" aria-hidden="true" />
            <a
              href="/docs/index.html"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-1.5 text-sm font-medium text-green/80 border border-green/20 rounded-lg hover:text-green hover:border-green/40 hover:bg-green/5 transition-all duration-200"
            >
              Docs
              <span className="sr-only"> (opens in new tab)</span>
            </a>
            <a
              href={RESUME_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-1.5 text-sm font-semibold text-surface bg-green rounded-lg hover:bg-green/90 btn-glow transition-all duration-200"
            >
              CV
              <span className="sr-only"> (opens in new tab)</span>
            </a>
            {rightSlot && (
              <>
                <div className="w-px h-4 bg-border mx-2" aria-hidden="true" />
                {rightSlot}
              </>
            )}
          </nav>

          {/* Hamburger — min 44x44 touch target */}
          <button
            ref={hamburgerRef}
            onClick={() => setMobileNavOpen(!mobileNavOpen)}
            className="md:hidden w-11 h-11 flex items-center justify-center -mr-2 rounded-md hover:bg-raised/60 transition-colors"
            aria-label={mobileNavOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileNavOpen}
          >
            <div className="w-5 flex flex-col gap-1">
              <span className={`h-0.5 w-full bg-txt transition-all duration-300 origin-center ${mobileNavOpen ? 'rotate-45 translate-y-[6px]' : ''}`} />
              <span className={`h-0.5 w-full bg-txt transition-all duration-300 ${mobileNavOpen ? 'opacity-0' : ''}`} />
              <span className={`h-0.5 w-full bg-txt transition-all duration-300 origin-center ${mobileNavOpen ? '-rotate-45 -translate-y-[6px]' : ''}`} />
            </div>
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileNavOpen && (
          <motion.nav
            ref={mobileNavRef}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="md:hidden border-b border-border/40 bg-surface/95 backdrop-blur-2xl overflow-hidden"
            aria-label="Mobile navigation"
          >
            <div className="px-6 py-4 space-y-1">
              {navItems.map(({ id, label }) => (
                <button
                  key={id}
                  onClick={() => handleNav(id)}
                  aria-current={activeSection === id ? 'page' : undefined}
                  className={`block w-full text-left px-3 py-3.5 text-sm rounded-md transition-colors ${
                    activeSection === id
                      ? 'text-green bg-green/5'
                      : 'text-txt-secondary hover:text-green hover:bg-raised/40'
                  }`}
                >
                  {label}
                </button>
              ))}
              <a
                href="/docs/index.html"
                target="_blank"
                rel="noopener noreferrer"
                className="block px-3 py-3.5 text-sm text-txt-secondary hover:text-txt rounded-md transition-colors"
              >
                Docs
                <span className="sr-only"> (opens in new tab)</span>
              </a>
              <a
                href={RESUME_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="block px-3 py-3.5 text-sm text-txt-secondary hover:text-txt rounded-md transition-colors"
              >
                CV
                <span className="sr-only"> (opens in new tab)</span>
              </a>
              <div className="pt-3">
                <button
                  onClick={() => handleNav('contact')}
                  className="w-full px-6 py-3 bg-green text-surface text-sm font-semibold rounded-lg btn-glow hover:bg-green/90 transition-all duration-300"
                >
                  Get In Touch
                </button>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Nav;
